import configs from '@/config'

/**
 * PostMessage Events Configuration for AI Chatbot
 * Manages cross-origin messaging with AI Chatbot app
 */

// PostMessage Event Types
export const POST_MESSAGE_EVENTS = {
  AI_IMPORT_REQUEST: 'AI_IMPORT_REQUEST',
}

// AI Chatbot Origins - matches the URL construction in ai-chatbot.module.js
export const ALLOWED_ORIGINS = {
  AI_CHATBOT: {
    // Get target origin matching the AI Chatbot URL construction
    getTargetOrigin() {
      if (!configs.aiAppUrl) {
        return null
      }
      try {
        return new URL(configs.aiAppUrl).origin
      } catch {
        // A misconfigured non-absolute value (legacy bare-hostname) would throw; degrade to null.
        return null
      }
    },
    // Get AI Chatbot iframe window reference for the import postMessage.
    //
    // AHA2-146: the AI_IMPORT_REQUEST handler is registered ONLY on the chatbot-app
    // Slide-agent surface (routes /slides and /slides-v5). When the presenter
    // tool-bridge flag (release-PRO7-presenter-sandbox-tool-bridge) is ON,
    // GlobalChatbotContainer docks a SECOND iframe — the /sandbox bridge — inside
    // .global-chatbot-container, and /sandbox has NO import handler. Targeting it
    // silently no-ops the import. So the import must reach the native Slide-agent
    // right-nav panel (AIAutoPanel), which the import caller mounts by opening the
    // AI tab — never the docked /sandbox iframe.
    //
    // (1) Prefer the native Slide-agent panel. AIAutoPanel renders it ONLY with the
    //     bridge ON and tags it data-testid="ai-auto-panel-slide-agent-iframe"; that
    //     attribute lands on the AIChatbotIframe wrapper DIV (Vue 2 fallthrough), so
    //     descend to the inner <iframe>. This DOM signal points at the handler-owning
    //     surface for BOTH v5 and non-v5 users — unlike a `/slides` src match, which
    //     misses the non-v5 Slide-agent URL (built at the root `/` path, see
    //     slideAgentIframeUrl / aiChatbotUrl).
    // (2) Otherwise fall back to the single legacy overlay iframe GlobalChatbotContainer
    //     mounts in .global-chatbot-container — the bridge-OFF path, which the native
    //     panel above never matches (it does not render with the bridge off), so this
    //     branch stays byte-for-byte identical to before. `v-show` sets display:none on
    //     the wrapper DIV not the inner <iframe>, so walk ancestors for an inline
    //     display:none (offsetParent is null on the fixed container); fall back to the
    //     first iframe.
    getTargetWindow() {
      const nativeSlideAgentIframe = document.querySelector(
        '[data-testid="ai-auto-panel-slide-agent-iframe"] iframe',
      )
      if (nativeSlideAgentIframe) {
        return nativeSlideAgentIframe.contentWindow || null
      }

      const globalChatbotContainer = document.querySelector('.global-chatbot-container')
      if (!globalChatbotContainer) {
        return null
      }

      const iframes = Array.from(globalChatbotContainer.querySelectorAll('iframe'))
      if (iframes.length === 0) {
        return null
      }

      const isHiddenByVShow = (iframe) => {
        let node = iframe
        // Inclusive of the container itself; stop once we step above it.
        while (node && node !== globalChatbotContainer.parentNode) {
          if (node.style && node.style.display === 'none') {
            return true
          }
          node = node.parentElement
        }
        return false
      }

      const visibleIframe = iframes.find((frame) => !isHiddenByVShow(frame))
      const targetIframe = visibleIframe || iframes[0]
      return targetIframe?.contentWindow || null
    },
  },
}

export default {
  POST_MESSAGE_EVENTS,
  ALLOWED_ORIGINS,
}
